/**
 * AssemblyLine — cinema wave-1 item 3: the pipeline board (consult idea #3).
 *
 * Six stage columns (PLAN → BUILD → GATE → MERGE → VERIFY → DEEP). Item chips
 * from items_summary physically sit in their current stage and FLIP-slide via
 * framer-motion `layout` when a poll reveals a stage advance. Stage headers
 * carry one dot per agent mapped to that stage, lit from the active run's
 * REAL sessions. No spinners, no progress bars, no elapsed-time motion.
 *
 * Derivation contract (all from real fields — docs/plans/2026-08-12-hud-cinema-consult.md):
 * - Dot lit     — a session of that agent_type exists with completed_at set.
 * - Dot pulsing — a session of that agent_type exists with completed_at null
 *                 (open). CSS keyframe at reduced opacity, reduced-motion safe.
 * - Dot dim     — no session, or sessions unavailable (degraded).
 * - Chip stage  — terminal item (status present in items_summary): its final
 *                 stage (status map below; 'merged' gets the MERGED ✓ stamp).
 *                 Non-terminal item during an active run: the stage of the
 *                 most recent mapped session's agent_type — the orchestrator
 *                 processes items SEQUENTIALLY (index.js executePhase item
 *                 loop), so the latest session IS the in-flight item's stage.
 *                 No mapped session yet / no sessions → PLAN (line intake) —
 *                 degrade toward truth, never toward theater.
 * - Amber tint  — an OPEN fixer/second_opinion session tints the column(s)
 *                 holding non-terminal chips (healing in progress). Never the
 *                 dot: the dot is the agent's own state, amber is the ITEM's.
 * - In-flight   — the executing stage shows 'IN SESSION Xm · last journal Ym
 *                 ago', both recomputed off real timestamps by useNow. The
 *                 interval ticks reality; it drives NO animation.
 * - Sweep       — one-shot highlight fired when a poll diff shows a chip's
 *                 stage advanced. Anchored to the data change event; the 900ms
 *                 timeout only REMOVES the class, it never schedules motion.
 *
 * Palette: near-black tokens + the brand orange accent. Amber (Tailwind
 * amber-500) is the spec-mandated healing tint ONLY. No cyan/teal/navy/gold,
 * no scanlines, no particles. prefers-reduced-motion collapses all motion to
 * a static board.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { FleetItemSummary, FleetSession } from '../../services/fleetHudApi';
import { useNow } from './fleetMood';
import { formatElapsedSeconds, parseTs } from './fleetTime';

/** Pipeline stages in display order. */
export const ASSEMBLY_STAGES = ['PLAN', 'BUILD', 'GATE', 'MERGE', 'VERIFY', 'DEEP'] as const;
export type AssemblyStage = (typeof ASSEMBLY_STAGES)[number];

/** Stage → agent_type map. Exact — mirrors the orchestrator pipeline roles. */
export const STAGE_AGENTS: Record<AssemblyStage, readonly string[]> = {
  PLAN: ['gap_author', 'plan_critic'],
  BUILD: ['executor'],
  GATE: ['schema_validator', 'code_reviewer', 'reality_verifier', 'impact_detector'],
  MERGE: ['merge_agent'],
  VERIFY: ['acceptance_probe', 'verifier'],
  // DEEP (fixed 2026-08-14 — the stage NEVER lit on any run): 'regression_review'
  // was a typo (the orch persists 'regression_reviewer', index.js:2420) and
  // senior_reviewer — the deep verdict that runs on EVERY run — was unmapped
  // (it sat in the constellation belt). warroom_verifier only fires on
  // forensically-relevant changes, so most runs' DEEP light IS senior_reviewer.
  DEEP: ['warroom_verifier', 'regression_reviewer', 'senior_reviewer'],
};

/** agent_type → stage, flattened from STAGE_AGENTS. */
const AGENT_STAGE: Record<string, AssemblyStage> = Object.entries(STAGE_AGENTS).reduce(
  (acc, [stage, agents]) => {
    for (const a of agents) acc[a] = stage as AssemblyStage;
    return acc;
  },
  {} as Record<string, AssemblyStage>
);

/** Open sessions of these types mean the fleet is repairing an item. */
const HEALING_AGENT_TYPES: ReadonlySet<string> = new Set(['fixer', 'second_opinion']);

/**
 * Terminal item status → final stage. Items die at the gates (schema /
 * code_review / reality / dead_code are all GATE agents), so GATE is the
 * honest fallback for terminal statuses without a known landing stage.
 */
const TERMINAL_STAGE: Record<string, AssemblyStage> = {
  merged: 'MERGE',
  healed: 'MERGE',
  pr_only_pending_review: 'MERGE',
  verified: 'VERIFY',
};
const TERMINAL_FALLBACK_STAGE: AssemblyStage = 'GATE';

/** Statuses that mean the item is done moving. */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'merged',
  'healed',
  'verified',
  'failed',
  'halted',
  'dead_code',
  'resolved',
  'rolled_back',
  'human_escalated',
  'preserved_for_reclaim',
  'cancelled',
  'pr_only_pending_review',
]);

interface ChipPlacement {
  id: string;
  item: FleetItemSummary;
  stage: AssemblyStage;
  terminal: boolean;
  merged: boolean;
  statusLabel: string | null;
}

type DotState = 'lit' | 'pulsing' | 'dim';

interface AssemblyLineProps {
  /** Active run id, or null when the fleet is idle. */
  activeRunId: string | null;
  /** Active run's sessions; undefined = unavailable/fetch failed → degrade. */
  sessions?: FleetSession[];
  /** items_summary from the latest run overview. */
  items?: FleetItemSummary[];
  /** Poll-time journal age in seconds (overview.journal_age_seconds). */
  journalAgeSeconds?: number | null;
  /** 'card' (default, wave-1 look) or 'band' — the bridge's squared,
      borderless full-width band under the core (hairline top only). */
  variant?: 'card' | 'band';
  /** Item 11: the stage selected in BridgeCore — its column is marked
      data-selected so the FleetHud spotlight fade skips it. */
  selectedStage?: string | null;
  /** Replay override: the VIRTUAL clock in epoch-ms. When set, every elapsed
      readout ('IN SESSION Xm', journal age) computes against it instead of
      wall-clock — a replayed 2:24pm session must read on the 2:24pm clock,
      not tonight's (the 'IN SESSION 11h 25m' artifact, 2026-08-14). Live
      views omit it and keep the useNow tick. */
  nowMs?: number;
  className?: string;
}

/** Most-recent-first recency key, same convention as fleetMood. */
function recency(s: FleetSession): number {
  return parseTs(s.started_at) ?? parseTs(s.updated_at) ?? 0;
}

/**
 * The pipeline stage a run is ACTUALLY in, derived from its sessions: the
 * most recent session whose agent maps to a stage wins (belt agents like
 * fixer/second_opinion don't move the needle). Exported (wave-3 polish) so
 * BridgeCore's center readout uses the same truth as the assembly board —
 * previously the core derived stage from the run's PHASE NUMBER and read
 * "PLAN" all the way through code review.
 */
export function stageFromSessions(sessions: FleetSession[]): AssemblyStage | null {
  const sorted = [...sessions].sort((a, b) => recency(b) - recency(a));
  for (const s of sorted) {
    const stage = s.agent_type ? AGENT_STAGE[s.agent_type] : undefined;
    if (stage) return stage;
  }
  return null;
}

function isOpen(s: FleetSession): boolean {
  return s.completed_at === null || s.completed_at === undefined;
}

/**
 * Component-scoped CSS. Pulse = open-session dot; sweep = one-shot stage
 * advance flash (iteration-count 1, never loops). The reduced-motion media
 * query collapses everything — the board stays fully functional and static.
 */
const AL_CSS = `
.al-dot--pulsing { animation: al-pulse 1.6s ease-in-out infinite; }
@keyframes al-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
.al-chip--flash { animation: al-sweep 0.9s ease-out 1; }
@keyframes al-sweep {
  0% { box-shadow: 0 0 0 0 rgba(255, 102, 0, 0.75); }
  100% { box-shadow: 0 0 16px 6px rgba(255, 102, 0, 0); }
}
/* Item 11: one-shot brightness pulse on the band column whose stage arc was
   clicked in BridgeCore. Gesture-bound, iteration-count 1, removed after
   750ms by the caller; the reduced-motion query below collapses it (and
   BridgeCore's JS half never adds the class under reduced motion). */
.al-column--flash { animation: al-column-flash 0.7s ease-out 1; }
@keyframes al-column-flash { 0% { filter: brightness(1.7); } 100% { filter: brightness(1); } }
@media (prefers-reduced-motion: reduce) {
  .al-root, .al-root * { animation: none !important; }
}
`;

export default function AssemblyLine({
  activeRunId,
  sessions,
  items,
  journalAgeSeconds,
  variant = 'card',
  selectedStage = null,
  nowMs,
  className,
}: AssemblyLineProps) {
  const reduced = useReducedMotion() ?? false;
  // Hook is unconditional (rules of hooks); replay's virtual clock wins when given.
  const realNow = useNow(1000);
  const now = nowMs ?? realNow;

  // Sessions for THIS run only (defensive: the endpoint is per-run, but a
  // session doc always carries run_id when written by the orchestrator).
  const runSessions = useMemo(() => {
    if (sessions === undefined) return undefined;
    return sessions.filter((s) => !s.run_id || !activeRunId || s.run_id === activeRunId);
  }, [sessions, activeRunId]);

  // Most recent session whose agent_type maps onto a stage = the stage the
  // run is executing now. Unmapped types (fixer, dependency_tracker, …) are
  // skipped so the board holds the last-KNOWN stage, not a guessed one.
  const currentStage: AssemblyStage | null = useMemo(
    () => (runSessions ? stageFromSessions(runSessions) : null),
    [runSessions]
  );

  // Latest OPEN session — anchors the 'IN SESSION Xm' elapsed readout.
  const openSession = useMemo(() => {
    if (!runSessions) return null;
    const open = runSessions.filter(isOpen).sort((a, b) => recency(b) - recency(a));
    return open[0] ?? null;
  }, [runSessions]);

  const healingOpen = useMemo(
    () => !!runSessions?.some((s) => isOpen(s) && !!s.agent_type && HEALING_AGENT_TYPES.has(s.agent_type)),
    [runSessions]
  );

  // Chip placements — pure derivation from props. IDLE fleet shows a CLEAN
  // board: with no active run, the latest TERMINAL run's items were rendered
  // as if live ('2 items under GATE despite idle' — owner, 2026-08-14); a
  // finished run's outcomes belong to RecentRuns/the build manifest, not the
  // live line. (The replay always passes its runId, so replays keep chips.)
  const chips: ChipPlacement[] = useMemo(() => {
    if (activeRunId === null) return [];
    return (items ?? []).map((item, i) => {
      const id = item.item ?? `item-${i}`;
      const status = item.status ?? null;
      const terminal = !!status && TERMINAL_STATUSES.has(status);
      if (terminal && status) {
        return {
          id,
          item,
          stage: TERMINAL_STAGE[status] ?? TERMINAL_FALLBACK_STAGE,
          terminal: true,
          merged: status === 'merged',
          statusLabel: status,
        };
      }
      // Non-terminal: during an active run the latest mapped session IS this
      // item's stage (sequential item loop). Otherwise line intake (PLAN).
      return {
        id,
        item,
        stage: currentStage ?? 'PLAN',
        terminal: false,
        merged: false,
        statusLabel: status,
      };
    });
  }, [items, currentStage, activeRunId]);

  // One-shot sweep: diff placements against the previous poll; a chip whose
  // stage index INCREASED flashes once. Anchored to the data change — the
  // timeout only removes the marker class.
  const [flashIds, setFlashIds] = useState<ReadonlySet<string>>(new Set());
  const prevStagesRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const prev = prevStagesRef.current;
    const advanced: string[] = [];
    for (const c of chips) {
      const idx = ASSEMBLY_STAGES.indexOf(c.stage);
      const prevIdx = prev.get(c.id);
      if (prevIdx !== undefined && idx > prevIdx) advanced.push(c.id);
      prev.set(c.id, idx);
    }
    if (advanced.length === 0 || reduced) return;
    setFlashIds(new Set(advanced));
    const id = window.setTimeout(() => setFlashIds(new Set()), 950);
    return () => window.clearTimeout(id);
  }, [chips, reduced]);

  // Journal age: poll-time base interpolated forward by the useNow tick
  // (same anchor pattern as ReactorCore's elapsed readout).
  const journalRef = useRef({ base: journalAgeSeconds ?? null, at: Date.now() });
  useEffect(() => {
    journalRef.current = { base: journalAgeSeconds ?? null, at: Date.now() };
  }, [journalAgeSeconds]);
  const journalAge =
    journalRef.current.base === null
      ? null
      : nowMs !== undefined
        ? Math.max(0, journalRef.current.base) // replay: age is already virtual — no wall-clock interpolation
        : Math.max(0, journalRef.current.base + (now - journalRef.current.at) / 1000);

  // Dot states per stage.
  const dotState = (agent: string): DotState => {
    if (!runSessions) return 'dim';
    const ofType = runSessions.filter((s) => s.agent_type === agent);
    if (ofType.length === 0) return 'dim';
    return ofType.some(isOpen) ? 'pulsing' : 'lit';
  };

  const chipsByStage = (stage: AssemblyStage) => chips.filter((c) => c.stage === stage);

  const openSessionMs = openSession ? recency(openSession) : null;
  const inSessionSeconds =
    openSessionMs !== null && openSessionMs > 0 ? Math.max(0, (now - openSessionMs) / 1000) : null;

  const degraded = activeRunId !== null && runSessions === undefined;

  const band = variant === 'band';
  return (
    <div
      className={`al-root ${
        band
          ? 'h-full border-t border-hairline bg-transparent px-4 py-3'
          : 'rounded-lg border border-hairline bg-surface px-4 py-3'
      } ${className ?? ''}`}
      data-testid="assembly-line"
      data-variant={variant}
    >
      <style>{AL_CSS}</style>
      <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-text-muted">
        assembly line
      </p>
      {/* Mobile-first: vertical stack at 390px; horizontal scroll-safe row
          from sm up, with a min-width per column so six stages never crush. */}
      <LayoutGroup>
        <div className="flex flex-col gap-3 sm:flex-row sm:overflow-x-auto">
          {ASSEMBLY_STAGES.map((stage) => {
            const stageChips = chipsByStage(stage);
            // Amber = an open fixer/second_opinion session AND this column
            // holds a non-terminal (in-flight) chip. Tints the COLUMN — the
            // item's location — never the agent dot.
            const healing = healingOpen && stageChips.some((c) => !c.terminal);
            const isExecuting =
              activeRunId !== null && !degraded && currentStage === stage && openSession !== null;
            return (
              <section
                key={stage}
                className={`min-w-0 border px-3 py-2 sm:flex-1 ${
                  band ? 'rounded-none sm:min-w-[150px]' : 'rounded sm:min-w-[168px]'
                } ${healing ? 'border-amber-500/60 bg-amber-500/5' : 'border-hairline bg-base'}`}
                data-testid={`al-stage-${stage}`}
                data-healing={healing ? 'true' : 'false'}
                data-stage-id={stage}
                data-selected={selectedStage === stage ? 'true' : undefined}
              >
                <header className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-text-primary">{stage}</span>
                  <span className="flex items-center gap-1.5">
                    {STAGE_AGENTS[stage].map((agent) => {
                      const state = dotState(agent);
                      return (
                        <span
                          key={agent}
                          title={agent}
                          className={`inline-block h-2 w-2 rounded-full ${
                            state === 'pulsing' ? 'al-dot--pulsing bg-brand' : ''
                          } ${
                            state === 'lit'
                              ? 'bg-brand'
                              : state === 'dim'
                                ? 'bg-text-ghost opacity-30'
                                : ''
                          }`}
                          data-testid={`al-dot-${stage}-${agent}`}
                          data-state={state}
                        />
                      );
                    })}
                  </span>
                </header>

                {/* In-flight truth text — silent, no spinners, no progress. */}
                {isExecuting && (
                  <p
                    className="mt-1 font-mono text-[10px] text-text-muted"
                    data-testid={`al-inflight-${stage}`}
                  >
                    IN SESSION {formatElapsedSeconds(inSessionSeconds)}
                    {journalAge !== null && ` · last journal ${formatElapsedSeconds(journalAge)} ago`}
                  </p>
                )}
                {/* Degraded fallback — static label, no fabricated state. */}
                {degraded && (
                  <p
                    className="mt-1 font-mono text-[10px] text-text-ghost"
                    data-testid={`al-fallback-${stage}`}
                  >
                    sessions unavailable
                  </p>
                )}

                <div className="mt-2 flex flex-col gap-1.5">
                  {stageChips.map((chip) => {
                    const flash = flashIds.has(chip.id);
                    const chipCls = `rounded border border-hairline bg-elevated px-2 py-1 font-mono text-[11px] text-text-secondary ${
                      flash && !reduced ? 'al-chip--flash' : ''
                    }`;
                    const body = (
                      <>
                        <span className="truncate">{chip.id}</span>
                        {chip.merged && (
                          <span className="ml-1.5 font-semibold text-brand">MERGED ✓</span>
                        )}
                        {chip.terminal && !chip.merged && chip.statusLabel && (
                          <span className="ml-1.5 text-text-muted">{chip.statusLabel}</span>
                        )}
                      </>
                    );
                    // prefers-reduced-motion: plain div, no layout animation —
                    // the static board. Otherwise FLIP-slide on stage change.
                    return reduced ? (
                      <div
                        key={chip.id}
                        className={chipCls}
                        data-testid={`al-chip-${chip.id}`}
                        data-stage={chip.stage}
                        data-flash="false"
                      >
                        {body}
                      </div>
                    ) : (
                      <motion.div
                        key={chip.id}
                        layout
                        layoutId={chip.id}
                        className={chipCls}
                        data-testid={`al-chip-${chip.id}`}
                        data-stage={chip.stage}
                        data-flash={flash ? 'true' : 'false'}
                      >
                        {body}
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </LayoutGroup>
    </div>
  );
}
